import { useState, forwardRef } from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import styled from '@emotion/styled';
import TextField from '@base/TextField/TextField';
import H6 from '@base/H6/H6';
import Image from '@base/Image';
import Close from '@icon/Close';
import { Spacings, breakpoints } from '@styles/index';
import { AnyArgVoidReturnFunction } from 'types/utils';

const TextFieldContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-left: ${Spacings.SPACING_4B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin-left: 12px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-left: 8px;
  }
`;

const StyledTextField = styled(TextField)<{ $textFieldCustomWidth: string }>`
  height: 48px;

  &.MuiInputBase-adornedEnd {
    padding-right: ${Spacings.SPACING_4B};
  }

  &.MuiOutlinedInput-root .MuiOutlinedInput-input {
    padding: 14px ${Spacings.SPACING_4B};
  }

  width: ${({ $textFieldCustomWidth }) => $textFieldCustomWidth};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 100%;

    &.MuiOutlinedInput-root {
      .MuiOutlinedInput-input {
        font-size: 14px;
      }
    }
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    &.MuiOutlinedInput-root {
      .MuiOutlinedInput-input {
        padding: 12px;
        font-size: 12px;
      }
    }
  }
`;

const Apply = styled(H6)`
  cursor: pointer;
`;

const ClearButton = styled(Close)`
  cursor: pointer;
`;

interface IReferralCodeInputProps {
  onApply: AnyArgVoidReturnFunction;
  resetReferral: AnyArgVoidReturnFunction;
  referralSubmitted?: boolean;
  error?: boolean;
  color?: string;
  defaultValue?: string;
  textFieldCustomWidth?: string;
}

const ReferralCodeInput = forwardRef<{}, IReferralCodeInputProps>(
  (
    {
      onApply,
      resetReferral,
      referralSubmitted = false,
      error = false,
      color = null,
      defaultValue = null,
      textFieldCustomWidth = '280px'
    },
    ref
  ) => {
    const [showApply, setShowApply] = useState(false);
    const [referralCode, setReferralCode] = useState(defaultValue);

    const handleApply = () => {
      if (onApply) {
        onApply(referralCode);
      }
    };

    const handleClearInput = () => {
      setReferralCode(null);
      setShowApply(false);
      if (resetReferral) {
        resetReferral();
      }
    };

    const endAdornment = () => {
      if (referralSubmitted) {
        return (
          <InputAdornment position="end">
            <ClearButton
              data-testid="clearReferral"
              size="24px"
              onClick={handleClearInput}
            />
          </InputAdornment>
        );
      }
      if (showApply) {
        return (
          <InputAdornment position="end">
            <Apply
              color="var(--color-i-primary)"
              onClick={handleApply}
              data-analytics="goal-applyReferral"
              data-testid="applyReferral"
            >
              Apply
            </Apply>
          </InputAdornment>
        );
      }
    };

    const onReferralCodeInputChange = () => {
      if (referralSubmitted) {
        resetReferral();
      }
      // @ts-ignore
      const code = ref.current?.value;
      if (code?.length > 0) {
        setShowApply(true);
      } else {
        setShowApply(false);
      }
      setReferralCode(code);
    };

    const onKeyPress = (key) => {
      if (key.key === 'Enter') {
        key.preventDefault();
        handleApply();
      }
    };

    return (
      <>
        <Image
          src="checkout/discount.svg"
          alt="discount"
          height={24}
          width={24}
        />
        <TextFieldContainer>
          <StyledTextField
            // @ts-ignore
            inputRef={ref}
            fullWidth
            error={error}
            color={color}
            disabled={referralSubmitted && !error}
            disabledColor="transparent"
            defaultValue={defaultValue}
            placeholder="Have a referral code?"
            endAdornment={endAdornment()}
            onChange={onReferralCodeInputChange}
            onKeyPress={onKeyPress}
            $textFieldCustomWidth={textFieldCustomWidth}
            inputProps={{ 'data-testid': 'referralInput' }}
          />
        </TextFieldContainer>
      </>
    );
  }
);

ReferralCodeInput.displayName = 'ReferralCodeInput';

export default ReferralCodeInput;
